import { isAdminDashboardPath } from "./assistantPageContext";

export const COPILOT_CHIP_COUNT = 4;

const DASHBOARD_STARTER_CHIPS = [
  "Which companies are paused?",
  "How many team vs company profiles exist?",
  "What is the Lite → Full conversion rate?",
  "Who has not started their assessment?",
];

const DASHBOARD_EXPLORATION_POOL = [
  "Which companies are paused?",
  "How many team vs company profiles exist?",
  "What is the Lite → Full conversion rate?",
  "Who has not started their assessment?",
  "How many steps are in the full assessment?",
  "Which companies are in my selection?",
  "What is the overall assessment completion rate?",
  "Give me one recommended admin action",
];

export function insightSuggestionChips(pathname) {
  if (isAdminDashboardPath(pathname)) {
    return DASHBOARD_STARTER_CHIPS;
  }
  if (pathname.includes("/portfolio")) {
    const performance = pathname.includes("lens=performance");
    return performance
      ? [
          "Which company has the lowest completion rate?",
          "What is the weakest pillar across the portfolio?",
          "Which company has the highest overall score?",
          "Summarize scores for the current filter",
        ]
      : [
          "Which pillar has the most drop-offs in the full assessment?",
          "What is the Lite → Full conversion rate?",
          "Who has not started their assessment?",
          "Summarize funnel health for the portfolio",
        ];
  }
  if (pathname.includes("/engagement")) {
    const pipeline = pathname.includes("lens=pipeline");
    return pipeline
      ? [
          "Who hasn't finished?",
          "Draft reminder emails for participants who opened but have not completed",
          "Which companies have the lowest completion rate?",
          "Summarize invite status for the current filter",
        ]
      : [
          "Which companies are in my selection?",
          "Who is already invited in this batch?",
          "Draft invite email for leadership team",
          "What roles can I assign to participants?",
        ];
  }
  if (pathname.includes("/studio")) {
    if (pathname.includes("lens=survey") || pathname.includes("/assessment-form")) {
      return [
        "How many steps are in the full assessment?",
        "What questions are on the current step?",
        "How does LITE differ from the full survey?",
        "Suggest clearer wording for drop-off questions",
      ];
    }
    if (pathname.includes("lens=reports") || pathname.includes("/report-profiles")) {
      return [
        "How many team vs company profiles exist?",
        "What blocks are in the default company profile?",
        "Create a team report profile for CEOs",
        "What is the difference between team and company reports?",
      ];
    }
    return [
      "Which companies are paused?",
      "Which companies have executive summaries ready?",
      "Summarize companies in the current filter",
      "How many companies are in the workspace?",
    ];
  }
  if (pathname.includes("/company-profile")) {
    return [
      "Which companies are paused?",
      "Which companies have executive summaries ready?",
      "Summarize companies in the current filter",
      "How many companies are in the workspace?",
    ];
  }
  if (pathname.includes("/report-profiles")) {
    return [
      "How many team vs company profiles exist?",
      "What blocks are in the default company profile?",
      "Which profile is best for board members?",
      "What is the difference between team and company reports?",
    ];
  }
  if (pathname.includes("/assessment-form")) {
    return [
      "How many steps are in the full assessment?",
      "What questions are on the current step?",
      "How does LITE differ from the full survey?",
      "How many total questions are in LITE?",
    ];
  }
  if (pathname.endsWith("/invites") || pathname.includes("/invites/")) {
    return [
      "Which companies have the lowest completion rate?",
      "Who has not started their assessment?",
      "Summarize invite status for the current filter",
      "Which companies have the highest completion rate?",
    ];
  }
  if (pathname.endsWith("/invite") || pathname.includes("/invite/")) {
    return [
      "Which companies are in my selection?",
      "Who is already invited in this batch?",
      "Any participants missing email addresses?",
      "What roles can I assign to participants?",
    ];
  }
  if (pathname.includes("/insights")) {
    return [
      "Which company has the lowest completion rate?",
      "What is the weakest pillar across the portfolio?",
      "Which company has the highest overall score?",
      "Summarize scores for the current filter",
    ];
  }
  if (pathname.includes("/analytics")) {
    return [
      "Which pillar has the most drop-offs in the full assessment?",
      "What is the Lite → Full conversion rate?",
      "How many participants are still pending?",
      "What is the overall assessment completion rate?",
    ];
  }
  return [
    "Summarize what is on this page",
    "What should I do next here?",
    "What can I do across the whole portfolio?",
    "Give me one recommended admin action",
  ];
}

export function normalizeQuestion(text) {
  return String(text || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function extractCompanyNames(answer) {
  const names = [];
  for (const line of String(answer || "").split("\n")) {
    const cleaned = line.replace(/^\s*[-•*]\s*/, "").trim();
    const m = cleaned.match(/^([^:(]+?)(?:\s*\(|:|\s+with\s+)/i);
    if (m) {
      const name = m[1].replace(/\*\*/g, "").trim();
      if (name.length > 1 && name.length < 80) names.push(name);
    }
  }
  return [...new Set(names)].slice(0, 3);
}

function extractNumbers(answer) {
  const nums = [];
  const re = /\b(\d+)\s*(company|team|profile|participant|invite|step|question)/gi;
  let m;
  while ((m = re.exec(answer))) {
    nums.push({ value: Number(m[1]), unit: m[2].toLowerCase() });
  }
  return nums;
}

function getExplorationPool(pathname) {
  if (isAdminDashboardPath(pathname)) {
    return DASHBOARD_EXPLORATION_POOL;
  }
  if (pathname.includes("/report-profiles")) {
    return [
      "Create company report profile for Board Member: pillars, gaps, variance — no individual breakdown",
      "Create team profile with pillar rankings and comments, no financials",
      "How many team vs company profiles exist?",
      "What blocks are in the default company profile?",
      "Which profile is best for board members?",
      "What is the difference between team and company reports?",
    ];
  }
  if (pathname.includes("/company-profile")) {
    return [
      "Which companies are paused?",
      "Which companies have executive summaries ready?",
      "Summarize companies in the current filter",
      "Which industries are most common in the catalog?",
      "How many companies are in the workspace?",
      "Which companies are missing logos?",
    ];
  }
  if (pathname.includes("/assessment-form")) {
    return [
      "How many steps are in the full assessment?",
      "What questions are on the current step?",
      "How does LITE differ from the full survey?",
      "How many total questions are in LITE?",
      "Can I edit question wording from here?",
    ];
  }
  if (pathname.endsWith("/invites") || pathname.includes("/invites/")) {
    return [
      "Which companies have the lowest completion rate?",
      "Who has not started their assessment?",
      "Summarize invite status for the current filter",
      "Which companies have the highest completion rate?",
      "How many participants are stuck in pending?",
      "Compare completion across the whole portfolio",
    ];
  }
  if (pathname.endsWith("/invite") || pathname.includes("/invite/")) {
    return [
      "Which companies are in my selection?",
      "Who is already invited in this batch?",
      "Any participants missing email addresses?",
      "What roles are available for participants?",
      "How do I add a new company to the wizard?",
    ];
  }
  if (pathname.includes("/insights")) {
    return [
      "Which company has the lowest completion rate?",
      "What is the weakest pillar across the portfolio?",
      "Which company has the highest overall score?",
      "Summarize scores for the current filter",
      "Compare pillar scores across the portfolio",
      "Which pillar has the widest gap between importance and agreement?",
      "How many companies are report-ready?",
      "Which companies have zero completions despite having invites?",
    ];
  }
  if (pathname.includes("/analytics")) {
    return [
      "Which pillar has the most drop-offs in the full assessment?",
      "What is the Lite → Full conversion rate?",
      "How many participants are still pending?",
      "What is the overall assessment completion rate?",
      "How many participants finished Lite but haven't started full?",
      "Which month had the most completions?",
      "What share of assessments are lite completed vs full completed?",
      "How many people chose to upgrade to the full assessment?",
    ];
  }
  return [
    "Summarize what is on this page",
    "What should I do next here?",
    "What can I do across the whole portfolio?",
    "Give me one recommended admin action",
  ];
}

function deriveFollowUps(pathname, question, answer, askedSet) {
  const q = normalizeQuestion(question);
  const candidates = [];
  const add = (text) => {
    const key = normalizeQuestion(text);
    if (!key || askedSet.has(key) || candidates.some((c) => normalizeQuestion(c) === key)) return;
    candidates.push(text);
  };

  const companies = extractCompanyNames(answer);
  const topCompany = companies[0];
  const nums = extractNumbers(answer);

  if (pathname.includes("/report-profiles")) {
    if (
      (q.includes("team") && q.includes("company")) ||
      q.includes("how many") ||
      q.includes("exist")
    ) {
      add("What blocks are in the default company profile?");
      add("Which profile is best for board members?");
      add("What is the difference between team and company report blocks?");
      add("Which profiles are system defaults vs custom?");
      if (nums.some((n) => n.unit === "company")) {
        add("List all company report profile names");
      }
      if (nums.some((n) => n.unit === "team")) {
        add("List all team report profile names");
      }
    } else if (q.includes("block")) {
      add("How many team vs company profiles exist?");
      add("Which profile is best for board members?");
      add("Which blocks are team-only vs company-only?");
      add("How do I change blocks in the profile editor?");
    } else if (q.includes("board")) {
      add("What blocks are in the default company profile?");
      add("How many team vs company profiles exist?");
      add("Which profile is best for CEO or leadership team?");
      add("Compare default profiles for each report type");
    } else {
      add("How many team vs company profiles exist?");
      add("What blocks are in the default company profile?");
      add("Which profile is best for board members?");
      add("What is the difference between team and company reports?");
    }
    return candidates;
  }

  if (pathname.includes("/invites") || pathname.endsWith("/invite")) {
    if (q.includes("lowest") && q.includes("completion")) {
      add("Which companies have the highest completion rate?");
      add("Who has not started at the lowest-completion companies?");
      if (topCompany) add(`Summarize all invites and status for ${topCompany}`);
      add("What should we do to improve completion for the bottom companies?");
    } else if (q.includes("not started")) {
      add("Which companies have the lowest completion rate overall?");
      add("How many participants are pending vs completed portfolio-wide?");
      if (topCompany) add(`Who is stuck at ${topCompany} and what is their status?`);
      add("Which companies have zero completions?");
    } else if (q.includes("highest") && q.includes("completion")) {
      add("Which companies have the lowest completion rate?");
      add("Who has not started across the portfolio?");
      add("Summarize invite status for the current filter");
    } else if (q.includes("summarize")) {
      add("Which companies have the lowest completion?");
      add("Who has completed their assessment in this view?");
      add("Compare completion rates across the whole portfolio");
      add("How many invites are pending portfolio-wide?");
    } else if (q.includes("selection") || q.includes("batch")) {
      add("Any participants missing roles or invalid emails?");
      add("Summarize invite completion for companies in my batch");
      add("Which companies are in my selection?");
    } else {
      add("Which companies have the lowest completion rate?");
      add("Who has not started their assessment?");
      add("Which companies have the highest completion rate?");
      add("Summarize invite status for the current filter");
    }
    return candidates;
  }

  if (pathname.includes("/insights")) {
    if (q.includes("completion") || q.includes("lowest") || q.includes("zero")) {
      add("Which companies have zero completions despite having invites?");
      add("Which company has the highest overall score?");
      add("How many companies are report-ready?");
      add("What is the weakest pillar portfolio-wide?");
    } else if (q.includes("pillar") || q.includes("weak") || q.includes("gap") || q.includes("benchmark")) {
      add("Which company has the lowest completion rate?");
      add("Which company has the highest overall score?");
      add("Compare top and bottom companies on overall score");
      add("Which pillar has the widest gap between importance and agreement?");
    } else if (q.includes("overall") || q.includes("highest") || q.includes("score")) {
      add("Which company has the lowest completion rate?");
      add("What is the weakest pillar across the portfolio?");
      add("Summarize scores for the current filter");
      if (topCompany) add(`What are ${topCompany}'s strongest and weakest pillars?`);
    } else if (q.includes("summarize") || q.includes("filter")) {
      add("Which company has the lowest completion rate?");
      add("What is the weakest pillar across the portfolio?");
      add("Which companies have zero completions despite having invites?");
    } else {
      add("Which company has the lowest completion rate?");
      add("What is the weakest pillar across the portfolio?");
      add("Summarize scores for the current filter");
      add("Which company has the highest overall score?");
    }
    return candidates;
  }

  if (pathname.includes("/analytics")) {
    if (q.includes("drop") || q.includes("funnel") || q.includes("pillar")) {
      add("What is the Lite → Full conversion rate?");
      add("How many participants are pending across the portfolio?");
      add("Which month had the most completions?");
      add("How many people are still deciding whether to upgrade to full?");
    } else if (q.includes("lite") || q.includes("conversion") || q.includes("upgrade")) {
      add("Which pillar has the most drop-offs in the full assessment?");
      add("How many participants finished Lite but haven't upgraded?");
      add("What is the overall assessment completion rate?");
      add("How many participants chose to upgrade later?");
    } else if (q.includes("pending") || q.includes("completion")) {
      add("Which pillar has the most drop-offs in the full assessment?");
      add("What is the Lite → Full conversion rate?");
      add("What share of assessments are lite completed vs full completed?");
    } else if (q.includes("month") || q.includes("week") || q.includes("trend")) {
      add("What is the overall assessment completion rate?");
      add("Which pillar has the most drop-offs?");
      add("How many participants are currently in progress?");
    } else {
      add("Which pillar has the most drop-offs in the full assessment?");
      add("What is the Lite → Full conversion rate?");
      add("How many participants are still pending?");
      add("What is the overall assessment completion rate?");
    }
    return candidates;
  }

  if (pathname.includes("/company-profile")) {
    if (q.includes("paused")) {
      add("Which companies have executive summaries ready?");
      add("List industries represented in the company catalog");
      add("How many companies are in the workspace?");
      add("Which paused companies have active invites?");
    } else if (q.includes("executive summary")) {
      add("Which companies are paused?");
      if (topCompany) add(`Is ${topCompany} ready for an executive summary PDF?`);
      add("How many companies have completed assessments?");
      add("Which companies lack executive summary readiness?");
    } else if (q.includes("summarize") || q.includes("filter")) {
      add("Which companies are paused?");
      add("Which companies have executive summaries ready?");
      add("What industries appear in the filtered list?");
    } else {
      add("Which companies are paused?");
      add("Which companies have executive summaries ready?");
      add("Summarize companies in the current filter");
      add("How many companies are in the catalog?");
    }
    return candidates;
  }

  if (pathname.includes("/assessment-form")) {
    if (q.includes("lite")) {
      add("How many steps are in the full assessment?");
      add("How many questions are in the LITE survey?");
      add("What questions are on the current step?");
      add("Can question text be edited after publish?");
    } else if (q.includes("step")) {
      add("How does LITE differ from the full survey?");
      add("How many steps are in the full assessment?");
      add("What is the title of the next step?");
    } else if (q.includes("how many")) {
      add("What questions are on the current step?");
      add("How does LITE differ from the full survey?");
      add("Which step has the most questions?");
    } else {
      add("How many steps are in the full assessment?");
      add("What questions are on the current step?");
      add("How does LITE differ from the full survey?");
      add("How many total questions are in the full survey?");
    }
    return candidates;
  }

  if (topCompany) add(`Tell me more about ${topCompany}`);
  add("What should I focus on next across the portfolio?");
  add("What is the most important follow-up from your last answer?");
  add("Give me one concrete recommended next step");
  return candidates;
}

function pushChip(merged, text, kind, askedSet) {
  const key = normalizeQuestion(text);
  if (!key || askedSet.has(key)) return false;
  if (merged.some((c) => normalizeQuestion(c.text) === key)) return false;
  merged.push({ text, kind });
  return true;
}

function fillToCount(merged, pathname, askedSet, maxChips, kind) {
  for (const text of getExplorationPool(pathname)) {
    if (merged.length >= maxChips) break;
    pushChip(merged, text, kind, askedSet);
  }
  return merged;
}

/**
 * Always up to 4 chips: contextual follow-ups first, then remaining starters, then padded from pool.
 */
export function getVisibleCopilotChips({
  pathname,
  askedQuestions = [],
  lastQuestion = "",
  lastAnswer = "",
  maxChips = COPILOT_CHIP_COUNT,
}) {
  const askedSet = new Set(askedQuestions.map(normalizeQuestion));
  const starters = insightSuggestionChips(pathname).filter(
    (chip) => !askedSet.has(normalizeQuestion(chip))
  );

  const hasConversation =
    lastQuestion && lastAnswer && !lastAnswer.startsWith("…") && lastAnswer.length > 10;

  const followUpTexts = hasConversation
    ? deriveFollowUps(pathname, lastQuestion, lastAnswer, askedSet)
    : [];

  const merged = [];
  for (const text of followUpTexts) {
    if (merged.length >= maxChips) break;
    pushChip(merged, text, "followup", askedSet);
  }

  const followUpKeys = new Set(followUpTexts.map(normalizeQuestion));
  for (const chip of starters) {
    if (merged.length >= maxChips) break;
    if (followUpKeys.has(normalizeQuestion(chip))) continue;
    pushChip(merged, chip, hasConversation ? "suggested" : "suggested", askedSet);
  }

  if (merged.length < maxChips) {
    fillToCount(merged, pathname, askedSet, maxChips, hasConversation ? "followup" : "suggested");
  }

  const hasFollowUpChips = merged.some((c) => c.kind === "followup");

  return {
    chips: merged.slice(0, maxChips),
    hasFollowUps: hasConversation && hasFollowUpChips,
    label: hasConversation ? "Follow up" : "Suggested",
  };
}
